import React, { useEffect, useMemo, useState } from 'react';

interface Props {
  vendorCode: string;
  vendorName: string;
}

interface Shipment {
  id: number;
  vendor_code: string;
  product_order_number: string;
  tracking_number: string;
  courier: string;
  recipient_name: string | null;
  recipient_phone: string | null;
  product_name: string | null;
  quantity: number | null;
  shipped_at: string;
}

function authHeaders(): Record<string, string> {
  return { Authorization: `Bearer ${sessionStorage.getItem('admin-token') || ''}` };
}

function currentMonth(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function formatShippedAt(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  const pad = (n: number) => (n < 10 ? `0${n}` : String(n));
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function SettlementPage({ vendorCode, vendorName }: Props) {
  const [month, setMonth] = useState<string>(currentMonth());
  const [rows, setRows] = useState<Shipment[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    setError('');
    fetch(`/api/vendor/settlements?vendorCode=${encodeURIComponent(vendorCode)}&month=${encodeURIComponent(month)}`, {
      headers: authHeaders(),
    })
      .then(async r => {
        if (!r.ok) throw new Error(await r.text());
        return r.json();
      })
      .then(d => setRows(Array.isArray(d.rows) ? d.rows : []))
      .catch(e => setError(e.message || String(e)))
      .finally(() => setLoading(false));
  }, [vendorCode, month]);

  const totalBoxes = useMemo(() => rows.reduce((s, r) => s + (r.quantity || 1), 0), [rows]);

  return (
    <div>
      <h1 className="text-2xl font-light text-gray-800 mb-6">{vendorName} 월말정산</h1>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <label className="text-xs text-gray-500">대상 월</label>
            <input
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-jeju-brown"
            />
            <span className="text-sm text-gray-500">발송 <strong className="text-gray-800">{rows.length}</strong>건 · 총 <strong className="text-gray-800">{totalBoxes}</strong>박스</span>
          </div>
        </div>

        {loading ? (
          <div className="px-6 py-12 text-center text-sm text-gray-400">불러오는 중...</div>
        ) : error ? (
          <div className="px-6 py-12 text-center text-sm text-red-500">{error}</div>
        ) : rows.length === 0 ? (
          <div className="px-6 py-12 text-center text-sm text-gray-400">해당 월의 발송 기록이 없습니다.</div>
        ) : (
          <div className="overflow-auto max-h-[calc(100vh-260px)]">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 border-b border-gray-200">발송일시</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 border-b border-gray-200">받는분</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 border-b border-gray-200">상품명</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 border-b border-gray-200">수량</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 border-b border-gray-200">상품주문번호</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 border-b border-gray-200">택배사</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 border-b border-gray-200">송장번호</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((r) => (
                  <tr key={r.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-2.5 text-gray-600 whitespace-nowrap text-xs">{formatShippedAt(r.shipped_at)}</td>
                    <td className="px-4 py-2.5 text-gray-800 font-medium whitespace-nowrap">{r.recipient_name || '-'}</td>
                    <td className="px-4 py-2.5 text-gray-700 max-w-[280px] truncate" title={r.product_name || ''}>{r.product_name || '-'}</td>
                    <td className="px-4 py-2.5 text-gray-800 text-right">{r.quantity ?? 1}</td>
                    <td className="px-4 py-2.5 text-gray-600 font-mono text-xs">{r.product_order_number}</td>
                    <td className="px-4 py-2.5 text-gray-600 whitespace-nowrap">{r.courier}</td>
                    <td className="px-4 py-2.5 text-gray-600 font-mono text-xs">{r.tracking_number}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
